import { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';

import './BimExtractModal.scss';
import JSONEditor from '../../../../../JSONEditor';
import SimpleModal from '../../../../../SimpleModal';

import { getExtractImportSchema, getExtractImportUiSchema } from './BimExtractSchema';

const BimMessage = ({ text, componentsOrMappings }) => (
  <div className="bim-message">
    <span className="bim-message-header">{text || 'Created or updated components:'}</span>
    <span>{componentsOrMappings?.length}</span>
  </div>
);

const BimExtractModal = ({
  onClose,
  onSubmit,
  onFormUpdate,
  bimComponents,
  componentsAllLevels,
  missingComponents,
  propertyNames,
  categoryNames,
  itemDesignationsCount,
  bimExtractConfig,
  importingBimData,
}) => {
  const [message, setMessage] = useState();
  const [formData, setFormData] = useState({});

  const defaultItemDesignation = propertyNames.some(
    (p) => bimExtractConfig.itemDesignationName === p
  )
    ? bimExtractConfig.itemDesignationName
    : '';
  const defaultCategory = categoryNames.some((c) =>
    bimExtractConfig.readCategories.some((rc) => c === rc)
  )
    ? bimExtractConfig.readCategories
    : [];
  const showUnmachedComponentsUi = itemDesignationsCount !== 0;

  const onFormSubmit = useCallback(async () => {
    onSubmit(formData.createUnmatchedComponents, bimComponents, componentsAllLevels);
  }, [formData, bimComponents, componentsAllLevels]);

  const showMessage = (validationMessage, filteredComponents, createMissingComponents) => {
    setMessage(
      <div className="message--container">
        <div>
          <p className="bim-validation-message">{validationMessage}</p>
          <BimMessage
            text="Created or updated mappings:"
            componentsOrMappings={bimComponents}
          />

          {!createMissingComponents && missingComponents && (
            <BimMessage
              text="Missing components:"
              componentsOrMappings={missingComponents}
            />
          )}

          {!createMissingComponents && filteredComponents.length > 0 && (
            <BimMessage componentsOrMappings={filteredComponents} />
          )}
          {createMissingComponents && (
            <div>
              <BimMessage componentsOrMappings={[...filteredComponents, ...missingComponents]} />
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const filteredComponents = bimComponents.filter(
      (mc) => !missingComponents.some((c) => c.itemDesignation === mc.itemDesignation)
    );

    const createMissingComponents = formData.createUnmatchedComponents;

    let validationMessage =
      bimComponents.length - itemDesignationsCount !== 0
        ? `There are ${itemDesignationsCount} valid and ${bimComponents.length - itemDesignationsCount} not valid reference designations in the model.`
        : `All reference designations are valid in the model.`;

    if (itemDesignationsCount === 0) {
      validationMessage = `There are no valid reference designations for this reference designation attribute in the model. Please select the correct attribute.`;
    }

    if (bimComponents.length - missingComponents.length === 0) {
      validationMessage = `Mappings cannot be created without adding missing components. Please check the box above to add them.`;
    }

    showMessage(validationMessage, filteredComponents, createMissingComponents);
  }, [itemDesignationsCount, missingComponents, bimComponents, formData]);

  const onHandleChange = useCallback(
    ({ formData: updatedFormData }) => {
      if (updatedFormData.itemDesignationName && updatedFormData.readCategories) {
        setFormData((fd) => ({ ...fd, ...updatedFormData }));
        onFormUpdate({
          itemDesignationName: updatedFormData.itemDesignationName,
          readCategories: updatedFormData.readCategories,
          categoryName: bimExtractConfig.categoryName,
        });
      }
    },
    [formData]
  );

  const onCancelImport = () => {
    onClose();
  };

  return (
    <SimpleModal
      className="bim-extract-modal"
      onClose={onClose}
      title="Import BIM data [Beta]"
    >
      <div className="json-editor">
        <JSONEditor
          formData={formData}
          schema={getExtractImportSchema(
            missingComponents.length,
            formData.createUnmatchedComponents,
            propertyNames,
            categoryNames,
            defaultItemDesignation,
            defaultCategory,
            showUnmachedComponentsUi
          )}
          uiSchema={getExtractImportUiSchema()}
          editorOnly
          cancelCallback={onCancelImport}
          onFormChange={onHandleChange}
          onFormSubmit={onFormSubmit}
          saveButtonText={'Import BIM data'}
          initialEditMode={true}
          showEditButton={false}
          customMessage={message}
          submitDisabled={
            itemDesignationsCount === 0 ||
            (bimComponents.length - missingComponents.length === 0 &&
              !formData.createUnmatchedComponents) ||
            importingBimData
          }
        />
      </div>
    </SimpleModal>
  );
};

BimExtractModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onFormUpdate: PropTypes.func.isRequired,
  componentsAllLevels: PropTypes.array.isRequired,
  bimComponents: PropTypes.array.isRequired,
  missingComponents: PropTypes.array.isRequired,
  bimExtractConfig: PropTypes.object.isRequired,
  itemDesignationsCount: PropTypes.number.isRequired,
};

export default BimExtractModal;
